import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import TimerOutlined from "@mui/icons-material/TimerOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import AnalyticsWidgetHeader from "./AnalyticsWidgetHeader";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  dashboardWidgetIconSx,
  DASHBOARD_LIST_WIDGET_HEIGHT,
} from "./dashboardWidgetLayout";
import { ElementType, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetAvgTimePerJobCardResponse } from "subModule/src/services/analytics/interface";
import { useAvgTimePerJobCardService } from "subModule/src/services/analytics/useAnalytics";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
interface AverageTimePerJCProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}
export default function AverageTimePerJC(props: AverageTimePerJCProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px",
  );
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const { refetch: apiRefetch } = useAvgTimePerJobCardService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });
  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) {
      setValue("range", updated);
    }
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);
  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetAvgTimePerJobCardResponse | unknown | any) => {
        if (res?.data?.success) {
          const raw = res?.data?.data || {};
          setData(raw);
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };
  return (
    <Card
      sx={{
        height: DASHBOARD_LIST_WIDGET_HEIGHT,
        minHeight: DASHBOARD_LIST_WIDGET_HEIGHT,
        boxShadow: "none",
        borderRadius: "8px",
        "& .MuiCardContent-root": { padding: "16px !important" },
      }}
    >
      <CardContent>
        <Box sx={{ ...dashboardWidgetHeaderSx, mb: "10px" }}>
          <AnalyticsWidgetHeader
            title={t("avgTimePerJC")}
            icon={<TimerOutlined />}
            iconBg={`${props.themes?.primary || "#4F46E5"}18`}
            iconColor={props.themes?.primary || "#4F46E5"}
          />
          <Box sx={dashboardWidgetFiltersSx}>
          <Controller
            name="stationId"
            control={control}
            defaultValue={null}
            rules={{ required: t("This field is required.") }}
            render={({ field }) => (
              <Box
                sx={{
                  "& .css-7896aj-control, & .css-1xiok1u-control": {
                    minHeight: "auto",
                    fontSize: '9px',
                    height: "auto",
                    overflow: "hidden",
                    "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                      {
                        background: "#fff",
                        padding: 0,
                        width: "15px",
                        height: "15px",
                      },
                    "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator":
                      { position: "absolute", right: 0 },
                    border: 0,
                  },
                }}
              >
                <DynamicSelect
                  label={""}
                  {...field}
                  options={props?.stations || []}
                  isDisabled={loading}
                  onChange={(selectedOption: any) => {
                    field.onChange(selectedOption);
                    if (selectedOption?.value === 0)
                      setValue("allStation", true);
                    else setValue("allStation", false);
                    setTimeout(() => fetchData(), 50);
                  }}
                  error={errors.stationId?.message as string}
                  ddWidth="30px"
                  ddHeight="30px"
                  defaultPlaceholder={t("station")}
                  isLoading={props.searchLoading === "avgTimePerJC"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      props.handleDropdownActions(
                        "avgTimePerJC",
                        "searchIt",
                        search,
                      );
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (props.dropdownSearchTerm || props.pageNo > 1)
                        props.handleDropdownActions(
                          "avgTimePerJC",
                          "searchIt",
                          "",
                        );
                      else props.setDropdownSearchTerm("");
                      props.setPageNo(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(props.stationsData?.pages[0]?.data as any).last)
                      props.handleDropdownActions("avgTimePerJC", "loadNext");
                  }}
                  page={props.pageNo}
                />
              </Box>
            )}
          />
          <Controller
            name="range"
            control={control}
            defaultValue={null}
            rules={{ required: t("This field is required.") }}
            render={({ field }) => (
              <Box
                sx={{
                  "& .css-7896aj-control, & .css-1xiok1u-control": {
                    width: screenWidth ? "60px" : "auto",
                    minHeight: "auto",
                    fontSize: '9px',
                    height: "auto",
                    overflow: "hidden",
                    "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                      {
                        background: "#fff",
                        padding: 0,
                        width: "15px",
                        height: "15px",
                      },
                    border: 0,
                  },
                }}
              >
                <DynamicSelect
                  label={""}
                  {...field}
                  options={filters}
                  onChange={(selectedOption: any) => {
                    field.onChange(selectedOption);
                    setTimeout(() => fetchData(), 50);
                  }}
                  error={errors.range?.message as string}
                  ddWidth="30px"
                  ddHeight="30px"
                  isDisabled={loading}
                />
              </Box>
            )}
          />
          </Box>
        </Box>
        {loading ? (
          <>
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
          </>
        ) : (
          <Box className="flex flex-col gap-2" sx={{ mt: 1.25 }}>
            {(
              [
                {
                  key: "avgTime",
                  labelKey: "avgTime",
                  value: (data?.avgTimeInMinutes || 0) / 60,
                  suffix: t("hours"),
                  color: props.themes?.primary || "#4F46E5",
                  Icon: AccessTimeOutlinedIcon,
                  fractionDigits: 1,
                },
                {
                  key: "totalJobs",
                  labelKey: "totalJobs",
                  value: Number(data?.count || 0),
                  suffix: "",
                  color: props.themes?.secondary || "#EA580C",
                  Icon: AssignmentOutlinedIcon,
                  fractionDigits: 2,
                },
                {
                  key: "totalTime",
                  labelKey: "totalTime",
                  value: (data?.sumOfTimeInMinutes || 0) / 60,
                  suffix: t("hours"),
                  color: "#1CACDC",
                  Icon: HourglassBottomOutlinedIcon,
                  fractionDigits: 1,
                },
              ] as {
                key: string;
                labelKey: string;
                value: number;
                suffix: string;
                color: string;
                Icon: ElementType;
                fractionDigits: number;
              }[]
            ).map((metric) => {
              const Icon = metric.Icon;
              const formatted = new Intl.NumberFormat("en", {
                notation: "compact",
                compactDisplay: "short",
                maximumFractionDigits: metric.fractionDigits,
              }).format(metric.value);
              return (
                <Box
                  key={metric.key}
                  className="flex items-center justify-between gap-1.5 rounded-[10px]"
                  sx={{
                    backgroundColor: `${metric.color}12`,
                    px: 1.25,
                    py: 1.25,
                    minWidth: 0,
                  }}
                >
                  <Box className="flex items-center gap-1.5 min-w-0">
                    <Box
                      sx={{
                        ...dashboardWidgetIconSx,
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: metric.color,
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 14, color: "#FFFFFF" }} />
                    </Box>
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#6B7280",
                        lineHeight: 1.25,
                      }}
                    >
                      {t(metric.labelKey)}
                    </Typography>
                  </Box>
                  <Tooltip
                    placement="top"
                    title={metric.value.toLocaleString()}
                    enterDelay={0}
                  >
                    <Typography
                      className="font-Saira"
                      sx={{
                        fontSize: 15,
                        fontWeight: 800,
                        color: metric.color,
                        lineHeight: 1.1,
                        flexShrink: 0,
                      }}
                    >
                      {formatted}
                      {metric.suffix ? ` ${metric.suffix}` : ""}
                    </Typography>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
